import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import jsonContent from './content.json';

export class UIManager {
    constructor({ visitedFromMobileDevice }) {
        this._visitedFromMobileDevice = visitedFromMobileDevice;
        this._overlayIsOpen = false;
        this._currentSectionIndex = 0;
        this._controls = null;
        this._isScrolling = false;

        // DOM references
        this._nav = document.querySelector('nav');
        this._secondaryNav = document.querySelector('.secondary-menu');
        this._footer = document.querySelector('footer');
        this._overlay = document.getElementById('overlay');
        this._closeBtn = document.getElementById('closeBtn');
        this._sectionScene = document.getElementById('container-opening-scene');
        this._sections = document.querySelectorAll('section');
        this._navItems = document.querySelectorAll('.nav-item');
        this._lines = document.querySelectorAll('.line');
        this._imageContainers = document.querySelectorAll('.image-container');

        // Content DOM references
        this._contentTitle = document.getElementById('content-title');
        this._contentVideo = document.getElementById('content-video');
        this._contentMainText = document.getElementById('content-main-txt');
        this._contentClient = document.getElementById('content-client');
        this._contentTech = document.getElementById('content-tech');
        this._headingLink = document.getElementById('heading-link');
        this._contentLink = document.getElementById('external-link');
        this._colorTxt = document.getElementsByClassName('txt-color');

        document.getElementById('currentYear').textContent = new Date().getFullYear();

        this._activeIndex = 0;
        this._targetSection = "#container-opening-scene";
        this._timeoutId = null;
        this._overlayTouchStart = 0;

        this._initNavVisibility();
        this._initLenis();
        this._initNavBarFade();
        this._initNavItemClicks();
        this._initWheelScroll();
        this._initOverlay();
        this._initContentLoading();
        this._initHideWasdIcon();
    }

    get overlayIsOpen() { return this._overlayIsOpen; }
    get currentSectionIndex() { return this._currentSectionIndex; }
    get lenisSite() { return this._lenisSite; }
    get lenisOverlay() { return this._lenisOverlay; }
    get sectionScene() { return this._sectionScene; }

    setControls(controls) {
        this._controls = controls;
    }

    // --- Nav Visibility ---
    _initNavVisibility() {
        if (!this._visitedFromMobileDevice) {
            this._nav.classList.remove('hide');
            this._secondaryNav.classList.remove('hide');
            this._footer.classList.remove('hide');
        }
        this._navItems[this._activeIndex].classList.add('active');
    }

    // --- Nav Bar Fade/Hover ---
    _initNavBarFade() {
        this._nav.addEventListener("mouseenter", () => this._fnResetTimer());
        document.addEventListener("wheel", () => this._fnResetTimer());
        this._fnResetTimer();
    }

    _fnHideNavBar() {
        this._nav.classList.add("hidden");
    }

    _fnResetTimer() {
        this._nav.classList.remove("hidden");
        clearTimeout(this._timeoutId);
        this._timeoutId = setTimeout(() => this._fnHideNavBar(), 3000);
    }

    // --- Nav Item Clicks & Highlight ---
    _initNavItemClicks() {
        this._navItems.forEach((item, index) => {
            item.addEventListener('click', () => {
                this._targetSection = this._navItems[index].dataset.targetsection;

                for (let i = 0; i < 3; i++) {
                    this._navItems[i].classList.remove('active');
                }

                this._lines[0].classList.remove('highlight-up', 'highlight-down');
                this._lines[1].classList.remove('highlight-up', 'highlight-down');

                item.classList.add('active');

                this._fnUpdateNavigation(index, this._activeIndex, this._lines);

                this._lenisSite.scrollTo(this._targetSection, { duration: 2.0, offset: 0 });
                this._activeIndex = index;
                this._targetSection = "";

                this._currentSectionIndex = this._activeIndex;
            });
        });
    }

    _fnUpdateNavigation(index, activeIndex, lines) {
        if (index > activeIndex) {
            for (let i = activeIndex; i < index; i++) {
                if (i === index - 1 && index != activeIndex + 1) {
                    setTimeout(() => {
                        lines[i].classList.add('highlight-down');
                    }, 600);
                } else {
                    lines[i].classList.add('highlight-down');
                }
            }
        }

        if (index < activeIndex) {
            for (let i = activeIndex - 1; i >= index; i--) {
                if (i === index && index != activeIndex - 1) {
                    setTimeout(() => {
                        lines[i].classList.add('highlight-up');
                    }, 600);
                } else {
                    lines[i].classList.add('highlight-up');
                }
            }
        }
    }

    // --- Lenis Setup ---
    _initLenis() {
        const lenisOverlayOptions = {
            duration: 1.75,
            direction: 'vertical',
            gestureDirection: 'vertical',
            smooth: true,
            mouseMultiplier: 1,
            smoothTouch: false,
            touchMultiplier: 2,
            infinite: false,
            wrapper: this._overlay,
            content: this._overlay.children[1]
        };

        const lenisSiteOptions = {
            duration: 1.25,
            smooth: true
        };

        this._lenisOverlay = new Lenis(lenisOverlayOptions);
        this._lenisSite = new Lenis(lenisSiteOptions);

        const raf = (time) => {
            this._lenisSite.raf(time);
            this._lenisOverlay.raf(time);
            requestAnimationFrame(raf);
        };
        requestAnimationFrame(raf);
    }

    // --- Wheel Scroll Sections ---
    _initWheelScroll() {
        window.addEventListener('wheel', (event) => {
            if (this._isScrolling) return;

            if (event.deltaY > 0 && !this._overlayIsOpen) {
                this._isScrolling = true;
                this._currentSectionIndex = Math.min(this._currentSectionIndex + 1, this._sections.length - 1);

                this._lines[this._currentSectionIndex - 1].classList.add('highlight-down');

                this._scrollToSection(this._currentSectionIndex);
                this._activeIndex = this._currentSectionIndex;

                this._navItems[this._currentSectionIndex].classList.add('active');
                this._navItems[this._currentSectionIndex - 1].classList.remove('active');

                setTimeout(() => {
                    this._lines[this._currentSectionIndex - 1].classList.remove('highlight-up', 'highlight-down');
                }, 600);

                if (this._controls !== null && this._controls !== undefined) {
                    this._controls.unlock();
                }

            } else if (event.deltaY < 0 && !this._overlayIsOpen) {
                this._isScrolling = true;
                this._currentSectionIndex = Math.max(this._currentSectionIndex - 1, 0);

                this._lines[this._currentSectionIndex].classList.add('highlight-up');

                this._scrollToSection(this._currentSectionIndex);
                this._activeIndex = this._currentSectionIndex;

                this._navItems[this._currentSectionIndex].classList.add('active');
                this._navItems[this._currentSectionIndex + 1].classList.remove('active');

                setTimeout(() => {
                    this._lines[this._currentSectionIndex].classList.remove('highlight-up', 'highlight-down');
                }, 600);
            }
        });
    }

    _scrollToSection(index) {
        this._isScrolling = true;
        if (index >= 0 && index < this._sections.length) {
            this._lenisSite.scrollTo(this._sections[index], {
                duration: 1.0,
                offset: 0,
                lock: true,
                onComplete: () => {
                    this._isScrolling = false;
                }
            });
        }
    }

    // --- Overlay Open/Close ---
    _initOverlay() {
        this._imageContainers.forEach(container => {
            container.addEventListener('click', () => {
                this._isScrolling = false;
                this._overlay.classList.add('show');
                this._lenisSite.stop();
                this._closeBtn.classList.add('show');

                this._overlayIsOpen = !this._overlayIsOpen;
                this._nav.classList.add("hide");
            });
        });

        this._closeBtn.addEventListener('click', () => {
            this._fnHandleCloseOverlay();
        });

        this._overlay.addEventListener('click', (e) => {
            e.stopPropagation();
            this._fnHandleCloseOverlay();
        });

        this._overlay.addEventListener('touchstart', (event) => {
            this._overlayTouchStart = event.touches[0].clientX;
        });

        this._overlay.addEventListener('touchmove', (e) => {
            const currentX = e.touches[0].clientX;
            const deltaX = currentX - this._overlayTouchStart;

            if (deltaX > 40) {
                this._overlay.classList.remove('show');
                this._closeBtn.classList.remove('show');

                this._lenisSite.start();
                setTimeout(() => {
                    this._lenisOverlay.scrollTo(0);
                }, 1000);
            }
        });
    }

    _fnHandleCloseOverlay() {
        this._isScrolling = false;
        this._overlayIsOpen = !this._overlayIsOpen;

        this._overlay.classList.remove('show');
        this._closeBtn.classList.remove('show');

        setTimeout(() => {
            this._lenisOverlay.scrollTo(0);
        }, 1000);

        if (!this._visitedFromMobileDevice) {
            this._nav.classList.remove("hide");
        }

        this._lenisSite.start();
    }

    // --- Content Loading ---
    _initContentLoading() {
        this._imageContainers.forEach(container => {
            container.addEventListener('click', async (e) => {
                const id = e.currentTarget.dataset.id;
                this._fnLoadContent(id);
            });
        });
    }

    _fnLoadContent(id) {
        const contentData = jsonContent[id];
        this._contentTitle.innerText = contentData.title;
        this._contentMainText.innerHTML = contentData.main_txt;
        this._contentClient.innerText = contentData.client;
        this._contentTech.innerHTML = contentData.tech;

        [...this._colorTxt].forEach((elem) => {
            const classes = elem.classList;
            classes.forEach(className => {
                if (className.startsWith('color-')) {
                    elem.classList.remove(className);
                }
            });
            elem.classList.add('color-' + id);
        });

        if (contentData.publications !== undefined) {
            this._headingLink.innerHTML = 'Publication';
            this._contentLink.innerHTML = contentData.publications;
        } else if (contentData.link !== undefined) {
            this._headingLink.innerHTML = 'Link';
            this._contentLink.innerHTML = contentData.link;
        } else {
            this._headingLink.innerHTML = '';
            this._contentLink.innerHTML = '';
        }

        // Create video element
        this._contentVideo.innerHTML = '';
        const videoElement = document.createElement('video');
        videoElement.autoplay = true;
        videoElement.playsInline = true;
        videoElement.loop = true;
        videoElement.muted = true;
        videoElement.setAttribute('width', '100%');

        const sourceElement = document.createElement('source');
        sourceElement.src = contentData.video_ref;
        sourceElement.type = 'video/webm';

        videoElement.appendChild(sourceElement);
        this._contentVideo.appendChild(videoElement);
        videoElement.load();

        for (let i = 1; i < 4; i++) {
            const showcaseImage = document.getElementById(`showcase-image-${i}`);

            if (contentData.images[i - 1]) {
                showcaseImage.src = contentData.images[i - 1];
                showcaseImage.alt = contentData.images_alt[i - 1];
                showcaseImage.type = "image/webp";
                showcaseImage.nextElementSibling.innerText = contentData.images_alt[i - 1];
            } else {
                showcaseImage.src = '';
                showcaseImage.alt = '';
                showcaseImage.nextElementSibling.innerText = '';
            }
        }
    }

    // --- WASD Icon Hide ---
    _initHideWasdIcon() {
        this._wasdHidden = false;
    }

    hideWasdIcon() {
        if (!this._wasdHidden) {
            this._wasdHidden = true;
            document.querySelector('.wasd-icon').classList.add('hide');
        }
    }
}
